import { observe, when } from 'mobx';
import { init } from '@sentry/react';
import { AnalyticsFactory, GA } from '@gamiphy/analytics';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import { UserStore } from '@gamiphy/loyalty-station-desktop/store/user';
import axios, { AxiosError } from 'axios';
import { Http } from '@gamiphy/http';
import { unstable_ClassNameGenerator as ClassNameGenerator } from '@mui/material/className';

const initializeMixpanel = async (token: string): Promise<void> => {
  const mixpanel = (await import('mixpanel-browser')).default;

  const urlParams = new URLSearchParams(window.location.search);
  const isSessionReplayEnabled = urlParams.get('enableSessionReplay') === 'true';

  mixpanel.init(token, {
    debug: false,
    track_pageview: true,
    record_sessions_percent: isSessionReplayEnabled ? 100 : 0,
  });
};

when(() => AppStore.initialized, () => {
  if (AppStore.app.id) {
    // Initialize mixpanel
    if (AppStore.app.featureFlags.mixpanelToken) {
      void initializeMixpanel(AppStore.app.featureFlags.mixpanelToken);
    }
    // Initialize Gamiphy analytics
    void AnalyticsFactory.initialize();
    // Initialize regular GA analytics
    GA.initialize(AppStore.app.id, process.env.TRACKING_ID, 'gamixTracker');
    // Start watching user changes
    observe(UserStore, 'user', () => {
      if (UserStore.user) {
        // Set user for Gamiphy analytics
        void AnalyticsFactory.setUserId(UserStore.user.id);
        // Set user for regular GA analytics
        GA.setUser(UserStore.user);
      }
    });
  }
});

init({
  environment: process.env.GAMIPHY_ENV,
  dsn: process.env.SENTRY_DSN,
  beforeSend: (event) => {
    if (IS_DEBUG) {
      // eslint-disable-next-line no-console
      console.error(event);
      return null; // this drops the event and nothing will be send to sentry
    }
    return event;
  },
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    'Can\'t find variable: ZiteReader',
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to
    // reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Exclude network errors
    'Network request failed',
    'Failed to fetch',
    'NetworkError',
    'Network Error',
    'withrealtime/messaging',
    'timeout of 0ms exceeded',
    'Request aborted',
    'ChunkLoadError',
  ],
  denyUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
  integrations(integrations) {
    // integrations will be all default integrations
    return integrations.filter((integration) => !['GlobalHandlers', 'TryCatch'].includes(integration.name));
  },
});

Http.onUnauthorized(() => {
  UserStore.logout();
});
axios.interceptors.response.use(undefined, (error: AxiosError) => {
  if (error.response?.status === 401) {
    UserStore.logout();
  }
  return Promise.reject(error);
});

ClassNameGenerator.configure(
  (componentName) => `c-${componentName}`,
);
